import { find } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { shopsApi } from 'api';
import { DISPUTE_TO_COMPLETED_ORDER_STATUS_REASONS } from 'constants/order.constants';
import {
  AdditionalOrderColumn,
  CallbackUrlStatus,
  FilterDefinitionType,
  OrdersTab,
  OrderStatusReason,
  QueryKey,
} from 'enums';
import { Orders } from 'features/orders';
import {
  useCurrencies,
  useOrderQueryFilters,
  useShopsQuery,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, OrderFilters } from 'types';
import { formUtils, orderUtils, requisitesUtils } from 'utils';

export const MerchantCompletedOrders: React.FC = () => {
  const { t: tFilters } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.filters',
  });

  const { paymentTypes, banks } = useUserContext();
  const { getFiatCurrencyCode, fiatCurrenciesOptions } = useCurrencies();

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsMerchant,
  );

  useOrderQueryFilters();

  const additionalFilters: FilterDefinition<OrderFilters>[] = useMemo(
    () => [
      {
        label: tFilters('customer'),
        name: 'customerId',
        type: FilterDefinitionType.Text,
      },
      {
        label: tFilters('shop'),
        name: 'shopId',
        type: FilterDefinitionType.Shop,
        shops: queryResultShops.data,
      },
      {
        label: tFilters('external_order_id'),
        name: 'externalOrderId',
        type: FilterDefinitionType.Text,
      },
      {
        label: tFilters('callback_url_status'),
        name: 'callbackUrlStatus',
        type: FilterDefinitionType.Enum,
        enum: CallbackUrlStatus,
        getDisplayName: orderUtils.getCallbackUrlStatusLabel,
      },
      {
        label: tFilters('payment_type'),
        name: 'paymentTypeId',
        type: FilterDefinitionType.Select,
        options: requisitesUtils.getPaymentTypesOptions(paymentTypes),
        getDisplayName: (value: string) =>
          requisitesUtils.getPaymentTypeLabel(
            find(paymentTypes, { id: value })!,
          ),
      },
      {
        label: tFilters('bank'),
        name: 'bankId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(banks),
        getDisplayName: (value: string) => find(banks, { id: value })?.name,
      },
      {
        label: tFilters('fiat_currency'),
        name: 'fiatCurrencyId',
        type: FilterDefinitionType.Select,
        options: fiatCurrenciesOptions,
        getDisplayName: getFiatCurrencyCode,
      },
      ...requisitesUtils.getRequisitesFieldsFilters<OrderFilters>(),
      {
        label: tFilters('status_reason'),
        name: 'statusReason',
        type: FilterDefinitionType.Enum,
        enum: DISPUTE_TO_COMPLETED_ORDER_STATUS_REASONS,
        getDisplayName: (value: OrderStatusReason) =>
          orderUtils.getStatusReasonLabel(value),
      },
    ],
    [
      banks,
      fiatCurrenciesOptions,
      getFiatCurrencyCode,
      paymentTypes,
      queryResultShops.data,
      tFilters,
    ],
  );

  return (
    <Orders
      tab={OrdersTab.Completed}
      additionalFilters={additionalFilters}
      additionalColumns={[
        AdditionalOrderColumn.CustomerContacts,
        AdditionalOrderColumn.Shop,
        AdditionalOrderColumn.ExternalOrderId,
        AdditionalOrderColumn.CustomerPayment,
      ]}
    />
  );
};
